/**
 * Audit Trail Formatters
 * Format dates and times for audit trail display
 */

/**
 * Format date as dd.mm.yyyy
 * @param date - ISO string or Date object
 * @returns Formatted date string (e.g., "10.10.2025")
 */
export function formatAuditDate(date: string | Date | undefined): string {
  if (!date) return '';
  
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(d.getTime())) return '';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    
    return `${day}.${month}.${year}`;
  } catch (error) {
    return '';
  }
}

/**
 * Format time as HH:mm (24-hour format)
 * @param date - ISO string or Date object
 * @returns Formatted time string (e.g., "14:30")
 */
export function formatAuditTime(date: string | Date | undefined): string {
  if (!date) return '';
  
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(d.getTime())) return '';
    
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return `${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
}

/**
 * Format date and time combined
 * @param date - ISO string or Date object
 * @returns Formatted date/time string (e.g., "10.10.2025 14:30")
 */
export function formatAuditDateTime(date: string | Date | undefined): string {
  if (!date) return '';
  
  const dateStr = formatAuditDate(date);
  const timeStr = formatAuditTime(date);
  
  if (!dateStr || !timeStr) return dateStr || timeStr || '';
  
  return `${dateStr} ${timeStr}`;
}

/**
 * Format nickname with fallback
 * @param nickname - User nickname
 * @returns Uppercase nickname or dash if empty
 */
export function formatNickname(nickname: string | undefined): string {
  if (!nickname || nickname.trim().length === 0) return '-';
  
  // If it's a long Firebase UID, shorten it
  if (nickname.length > 10) {
    return nickname.substring(0, 3).toUpperCase() + '...';
  }
  
  return nickname.toUpperCase();
}

/**
 * Get current timestamp in ISO format
 * @returns ISO string of current date/time
 */
export function getCurrentAuditTimestamp(): string {
  return new Date().toISOString();
}

